import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLanguage } from "../context/LanguageContext";
import PublicNav from "../components/PublicNav";
import LanguageSelector from "../components/LanguageSelector";
import LegalEntityDisclosure from "../components/LegalEntityDisclosure";
import api from "../lib/api";
import { 
  ArrowLeft,
  Calendar,
  Clock,
  Video,
  Phone,
  CheckCircle2,
  Shield,
  Users,
  MessageCircle,
  Mail,
  ExternalLink,
  Play,
  PhoneCall
} from "lucide-react";

// Microsoft 365 Bookings URL — single source of truth for scheduling.
// Mirrors /book route. Microsoft CSP blocks iframe embedding, so we
// open it in a new tab (Microsoft's recommended pattern).
const MS_BOOKINGS_URL =
  "https://outlook.office.com/bookwithme/user/efddaaf235c647b6869642090f8b105e@eqhosensecapital.com?anonymous&ismsaljsauthenabled&ep=plink";

const ConsultationPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [introVideoUrl, setIntroVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState('call'); // 'call' or 'video'

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/public');
        if (response.data?.calendly_url) {
          setCalendlyUrl(response.data.calendly_url);
        }
        if (response.data?.intro_video_url) {
          setIntroVideoUrl(response.data.intro_video_url);
        }
      } catch (error) {
        console.log('Settings not configured');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src*="calendly"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: language === 'en' ? "Compliance First" : "Cumplimiento Primero",
      description: language === 'en' 
        ? "We'll explain how EchoSense Capital Club works as an introducer tracking platform, separate from investment operations."
        : "Explicaremos cómo funciona EchoSense Capital Club como plataforma de seguimiento de introductores, separada de operaciones de inversión."
    },
    {
      icon: Users,
      title: language === 'en' ? "No Obligation" : "Sin Compromiso",
      description: language === 'en'
        ? "Our consultations are completely free with no pressure. We believe in building trust first."
        : "Nuestras consultas son completamente gratuitas sin presión. Creemos en construir confianza primero."
    },
    {
      icon: Video,
      title: language === 'en' ? "Flexible Format" : "Formato Flexible",
      description: language === 'en'
        ? "Choose between video call, phone call, or email—whatever works best for you."
        : "Elige entre videollamada, llamada telefónica o correo electrónico—lo que mejor te funcione."
    }
  ];

  const topics = language === 'en' ? [
    "How EchoSense Capital Club tracks referrals and introductions",
    "Early KYC process and why it matters",
    "Points system for tracking activity",
    "Compliance requirements for introducers",
    "Getting started with your account",
    "Questions about the platform"
  ] : [
    "Cómo EchoSense Capital Club rastrea referencias e introducciones",
    "Proceso de KYC temprano y por qué importa",
    "Sistema de puntos para rastrear actividad",
    "Requisitos de cumplimiento para introductores",
    "Comenzar con tu cuenta",
    "Preguntas sobre la plataforma"
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <PublicNav active="consultation" />

      {/* Hero Section */}
      <section className="py-16 px-8 border-b border-platinum/10">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-platinum/60 hover:text-platinum mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} className="mr-2" />
            {t('common.back')}
          </Button>
          
          <div className="max-w-2xl">
            <p className="text-gold uppercase tracking-[0.35em] text-xs font-semibold mb-4">
              {language === 'en' ? 'Free Consultation' : 'Consulta Gratuita'}
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold text-cream font-serif leading-tight">
              {language === 'en' ? (
                <>Schedule Your <span className="gradient-platinum">Discovery Call</span></>
              ) : (
                <>Agenda Tu <span className="gradient-platinum">Llamada de Descubrimiento</span></>
              )}
            </h1>
            <p className="mt-6 text-lg text-platinum/80 leading-relaxed">
              {language === 'en' 
                ? "Learn how EchoSense Capital Club helps you track referrals and manage compliance. No obligation, no pressure—just honest conversation about the platform."
                : "Aprende cómo EchoSense Capital Club te ayuda a rastrear referencias y gestionar el cumplimiento. Sin compromiso, sin presión—solo una conversación honesta sobre la plataforma."
              }
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div className="space-y-8">
              {/* Benefits */}
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-cream font-serif">
                  {language === 'en' ? 'What to Expect' : 'Qué Esperar'}
                </h2>
                {benefits.map((benefit, index) => (
                  <div key={benefit.title || index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="text-gold" size={20} />
                    </div>
                    <div>
                      <h3 className="text-cream font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-platinum/70 text-sm leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Topics */}
              <Card className="bg-surface-highlight border-gold/15">
                <CardContent className="p-6">
                  <h3 className="text-cream font-semibold mb-4 flex items-center gap-2 font-serif">
                    <MessageCircle className="text-gold" size={20} />
                    {language === 'en' ? "Topics We'll Cover" : 'Temas que Cubriremos'}
                  </h3>
                  <ul className="space-y-3">
                    {topics.map((topic, index) => (
                      <li key={`${topic}-${index}`} className="flex items-center gap-3 text-platinum/85 text-sm">
                        <CheckCircle2 className="text-emerald flex-shrink-0" size={16} />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Duration & Format */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-surface border border-platinum/15">
                  <Clock className="text-gold mb-2" size={24} />
                  <p className="text-cream font-semibold">{language === 'en' ? '30 Minutes' : '30 Minutos'}</p>
                  <p className="text-platinum/60 text-sm">{language === 'en' ? 'Typical duration' : 'Duración típica'}</p>
                </div>
                <div className="p-4 rounded-lg bg-surface border border-platinum/15">
                  <Video className="text-gold mb-2" size={24} />
                  <p className="text-cream font-semibold">{language === 'en' ? 'Video or Phone' : 'Video o Teléfono'}</p>
                  <p className="text-platinum/60 text-sm">{language === 'en' ? 'Your choice' : 'Tu elección'}</p>
                </div>
              </div>
            </div>

            {/* Right Column - Option Selector + Content */}
            <div>
              {/* Option Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setSelectedOption('call')}
                  className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border transition-all ${
                    selectedOption === 'call'
                      ? 'bg-gold/15 border-gold text-cream font-semibold shadow-lg shadow-gold/10'
                      : 'bg-surface-highlight/60 border-platinum/15 text-platinum/80 hover:border-gold/40 hover:text-cream'
                  }`}
                  data-testid="option-call"
                >
                  <PhoneCall size={20} className={selectedOption === 'call' ? 'text-gold' : 'text-platinum/70'} />
                  <div className="text-left">
                    <p className="font-semibold">{language === 'en' ? '30-Min Intro Call' : 'Llamada de 30 Min'}</p>
                    <p className="text-xs opacity-80">{language === 'en' ? 'Schedule a video/phone call' : 'Agenda una llamada'}</p>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedOption('video')}
                  className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border transition-all ${
                    selectedOption === 'video'
                      ? 'bg-gold/15 border-gold text-cream font-semibold shadow-lg shadow-gold/10'
                      : 'bg-surface-highlight/60 border-platinum/15 text-platinum/80 hover:border-gold/40 hover:text-cream'
                  }`}
                  data-testid="option-video"
                >
                  <Play size={20} className={selectedOption === 'video' ? 'text-gold' : 'text-platinum/70'} />
                  <div className="text-left">
                    <p className="font-semibold">{language === 'en' ? 'Watch Intro Video' : 'Ver Video de Intro'}</p>
                    <p className="text-xs opacity-80">{language === 'en' ? 'Learn at your own pace' : 'Aprende a tu ritmo'}</p>
                  </div>
                </button>
              </div>

              <Card className="bg-gradient-to-b from-surface to-surface-highlight border-gold/25 overflow-hidden shadow-xl">
                <CardContent className="p-0">
                  {selectedOption === 'call' ? (
                    // Call/MS Bookings Section
                    <>
                      {loading ? (
                        <div className="h-[550px] flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-platinum/70">{language === 'en' ? 'Loading scheduler...' : 'Cargando programador...'}</p>
                          </div>
                        </div>
                      ) : calendlyUrl ? (
                        <div 
                          className="calendly-inline-widget" 
                          data-url={`${calendlyUrl}?hide_gdpr_banner=1&background_color=0B1220&text_color=E5E4E2&primary_color=EAB308`}
                          style={{ minWidth: '320px', height: '580px' }}
                        />
                      ) : (
                        // Production scheduler — MS 365 Bookings (active).
                        // Microsoft CSP blocks iframe embedding, so we present
                        // a high-contrast Goldman-grade card that launches the
                        // booking page in a new tab — the official pattern.
                        <div className="min-h-[550px] flex items-center justify-center p-8">
                          <div className="text-center max-w-sm w-full">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold/40 bg-surface-highlight mb-6">
                              <Calendar className="text-gold" size={28} />
                            </div>
                            <p className="text-gold text-[11px] tracking-[0.35em] uppercase font-semibold mb-3">
                              {language === 'en' ? 'Private Booking' : 'Reserva Privada'}
                            </p>
                            <h3 className="text-cream font-serif font-semibold text-2xl mb-3">
                              {language === 'en' ? 'Open Your Private Calendar' : 'Abre Tu Calendario Privado'}
                            </h3>
                            <p className="text-platinum/75 text-sm mb-7 leading-relaxed">
                              {language === 'en' 
                                ? 'You will be taken to our secure Microsoft 365 booking page in a new tab. Pick your executive and an available 30-minute slot.'
                                : 'Te llevaremos a nuestra página de reservas Microsoft 365 segura en una pestaña nueva. Elige a tu ejecutivo y un horario disponible de 30 minutos.'
                              }
                            </p>

                            <a
                              href={MS_BOOKINGS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-3 w-full bg-gold hover:bg-gold/90 text-navy font-semibold tracking-widest uppercase text-sm px-8 py-4 rounded transition-all hover:shadow-lg hover:shadow-gold/30"
                              data-testid="consultation-book-cta"
                            >
                              <Calendar className="w-4 h-4" />
                              {language === 'en' ? 'Book Your Consultation' : 'Reserva Tu Consulta'}
                              <ExternalLink className="w-4 h-4 opacity-70" />
                            </a>

                            <div className="mt-6 pt-6 border-t border-platinum/10 space-y-3">
                              <p className="text-platinum/50 text-[11px] uppercase tracking-widest mb-3">
                                {language === 'en' ? 'Prefer to reach us directly' : 'Prefieres contactarnos directamente'}
                              </p>
                              <a 
                                href="mailto:admin@echosensecapital.com?subject=30-Min%20Intro%20Call%20Request"
                                className="flex items-center justify-center gap-3 w-full p-3 rounded-lg bg-surface/60 border border-platinum/15 text-platinum hover:border-gold/40 hover:text-cream transition-colors text-sm"
                              >
                                <Mail size={16} className="text-gold" />
                                <span>admin@echosensecapital.com</span>
                              </a>
                              <a 
                                href="tel:+17025180208"
                                className="flex items-center justify-center gap-3 w-full p-3 rounded-lg bg-surface/60 border border-platinum/15 text-platinum hover:border-gold/40 hover:text-cream transition-colors text-sm"
                              >
                                <Phone size={16} className="text-gold" />
                                <span>(702) 518-0208</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    // Video Section
                    <div className="min-h-[550px] flex items-center justify-center p-8">
                      {introVideoUrl ? (
                        <div className="w-full h-full">
                          <video
                            controls
                            className="w-full h-full rounded-lg object-cover"
                            poster="/video-poster.jpg"
                          >
                            <source src={introVideoUrl} type="video/mp4" />
                            {language === 'en' 
                              ? 'Your browser does not support the video tag.'
                              : 'Tu navegador no soporta la etiqueta de video.'
                            }
                          </video>
                        </div>
                      ) : (
                        <div className="text-center max-w-sm">
                          <div className="w-24 h-24 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mx-auto mb-6">
                            <Play className="text-gold" size={40} />
                          </div>
                          <h3 className="text-cream font-serif font-semibold text-2xl mb-3">
                            {language === 'en' ? 'Intro Video Coming Soon' : 'Video de Intro Próximamente'}
                          </h3>
                          <p className="text-platinum/75 text-sm mb-7 leading-relaxed">
                            {language === 'en' 
                              ? "We're preparing a comprehensive introduction video. In the meantime, schedule a personal call with our team."
                              : 'Estamos preparando un video de introducción completo. Mientras tanto, agenda una llamada personal con nuestro equipo.'
                            }
                          </p>
                          
                          <div className="space-y-3">
                            <Button 
                              className="w-full bg-gold hover:bg-gold/90 text-navy font-semibold tracking-wide shadow-lg shadow-gold/20"
                              onClick={() => setSelectedOption('call')}
                            >
                              <PhoneCall size={18} className="mr-2" />
                              {language === 'en' ? 'Schedule a Call Instead' : 'Agendar Llamada'}
                            </Button>
                            <Button 
                              variant="outline"
                              className="w-full border-platinum/25 text-platinum hover:bg-platinum/10 hover:text-cream"
                              onClick={() => navigate("/register")}
                            >
                              {t('common.get_started')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Admin note */}
              {!loading && (
                <p className="text-platinum/30 text-xs text-center mt-4">
                  {language === 'en' 
                    ? 'Admin: Override the MS Bookings link via Settings → Engagement Tools (Calendly URL field).'
                    : 'Admin: Sobrescribe el enlace de MS Bookings via Configuración → Herramientas (campo Calendly URL).'
                  }
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial/Trust Section */}
      <section className="py-16 px-8 bg-surface-highlight/60 border-t border-platinum/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-platinum/70 italic text-lg mb-4">
            {language === 'en'
              ? "\"Our goal is to help you understand the platform before you get started. We believe in transparency and compliance first.\""
              : "\"Nuestro objetivo es ayudarte a entender la plataforma antes de comenzar. Creemos en la transparencia y el cumplimiento primero.\""
            }
          </p>
          <p className="text-platinum/50 text-sm">— EchoSense Capital Club Team</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8 bg-obsidian border-t border-platinum/10">
        <div className="max-w-7xl mx-auto space-y-3">
          <LegalEntityDisclosure variant="footer" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <p className="text-platinum/40 text-xs">
                © {new Date().getFullYear()} EchoSense Capital Club. {language === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}{' · '}<Link to="/disclaimer" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-disclaimer">{language === 'en' ? 'Disclaimer' : 'Aviso legal'}</Link>
              </p>
              <LanguageSelector variant="minimal" />
            </div>
            <p className="text-platinum/30 text-xs text-center max-w-xl">
              <strong>{language === 'en' ? 'Note:' : 'Nota:'}</strong> {t('about.conclusion_content')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConsultationPage;
